import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs/Observable'
import { environment } from '../../../environments/environment'

export interface NbTracklist {
  id: number
  score: number
  mixId: number
  time: number
  artist: string
  track: string
  album: string
  isrc: string
  exercise?: string
  bpm?: number
}

export interface NbResource {
  product: {
    grid: string;
    icpn: string;
    catalogNumber: string;
  }
  productTitle: string
  productType: 'Album' | 'Single'
  isrc: string
  format: 'sound' | 'video' | 'image' | 'text'
  title: string
  artist: string
  duration: string
  isActive: boolean
  provider: 'SONY' | 'UMG'
  id: string
  score: number
}

export interface NbAutoMatch extends NbTracklist {
  matchArtist: string
  matchTitle: string
}

@Injectable()
export class TracklistService {
  private compareTwoStrings(str1: string, str2: string) {
    const first = str1.replace(/\s+/g, '')
    const second = str2.replace(/\s+/g, '')

    if (!first.length && !second.length) return 1 // if both are empty strings
    if (!first.length || !second.length) return 0 // if only one is empty string
    if (first === second) return 1 // identical
    if (first.length === 1 && second.length === 1) return 0 // both are 1-letter strings
    if (first.length < 2 || second.length < 2) return 0 // if either is a 1-letter string

    const firstBigrams = new Map()
    for (let i = 0; i < first.length - 1; i++) {
      const bigram = first.substr(i, 2)
      const count = firstBigrams.has(bigram) ? firstBigrams.get(bigram) + 1 : 1

      firstBigrams.set(bigram, count)
    }

    let intersectionSize = 0
    for (let i = 0; i < second.length - 1; i++) {
      const bigram = second.substr(i, 2)
      const count = firstBigrams.has(bigram) ? firstBigrams.get(bigram) : 0

      if (count > 0) {
        firstBigrams.set(bigram, count - 1)
        intersectionSize++
      }
    }

    return (2.0 * intersectionSize) / (first.length + second.length - 2)
  }

  constructor(private http: HttpClient) {}

  public getTrack(startId = 0, skip = 0): Observable<NbTracklist> {
    return this.http
      .post(`${environment.DDEX}/track-fetch`, { startId, skip })
      .map((res: { track: NbTracklist; success: boolean }) => res.track)
  }

  public search(track: {
    artist: string;
    track: string;
  }): Observable<{ tracks: NbTracklist[]; resources: NbResource[] }> {
    return this.http
      .post(`${environment.DDEX}/search`, { ...track, size: 100 })
      .map(
        (res: {
          results: { resources: NbResource[]; tracks: NbTracklist[] };
          success: boolean;
          statusCode: number;
        }) => res.results,
      )
  }

  public saveMatches(
    tracks: NbTracklist[],
    resource: NbResource,
  ): Observable<boolean> {
    return this.http
      .post(`${environment.DDEX}/match-assign`, { tracks, resource })
      .map((res: { success: boolean }) => res.success)
  }

  public fetchMatch(range: {
    start: number;
    count: number;
  }): Observable<NbAutoMatch[]> {
    return this.http
      .post(`${environment.DDEX}/match-fetch`, range)
      .map((res: { results: NbAutoMatch[] }) => res.results)
  }

  public deleteMatch(trackId: number): Observable<boolean> {
    return this.http
      .post(`${environment.DDEX}/match-delete`, { trackId })
      .map((res: { success: boolean }) => res.success)
  }

  public getReport(range: {
    from: string;
    to: string;
  }): Observable<
    Array<{
      _id: { vendor: number; sony: boolean; country: string };
      count: number;
      totalMins: number;
    }>
  > {
    return this.http
      .post(`${environment.DDEX}/report-finance`, { ...range })
      .map((res: any) => res.result)
  }

  public getUserStats(range: {
    from: string;
    to: string;
  }): Observable<
    Array<{
      vendor: number;
      count: number;
      country: string;
      new: string;
      churn: number;
      one_time: number;
    }>
  > {
    return this.http
      .get(
        `${environment.HOST}/api2.0/general/reportingtypefill/start_date/${range.from}/end_date/${range.to}`,
      )
      .map((res: any) => res.results)
  }

  public generateReport(
    range: { from: string; to: string },
    data: any,
  ): Observable<any> {
    return this.http
      .post(`${environment.DDEX}/report-generate`, { ...range, data })
      .map((res: any) => res.result)
  }

  public calcSimilarity(str1: string, str2: string) {
    const removeWords = ['a', 'an', 'the', 'and', 'x', 'vs']

    return this.compareTwoStrings(
      str1
        .toLowerCase()
        .split(' ')
        .filter(word => !removeWords.includes(word))
        .join('')
        .replace(/[^0-9a-z]/gi, ''),
      str2
        .toLowerCase()
        .split(' ')
        .filter(word => !removeWords.includes(word))
        .join('')
        .replace(/[^0-9a-z]/gi, ''),
    )
  }
}



// WEBPACK FOOTER //
// /srv/staging/FitRadioAdmin2/src/app/@core/data/tracklist.service.ts